import AsyncStorage from '@react-native-async-storage/async-storage';
import { isSensitiveOnlineRequiredRoute, isMutationMethod } from './offlinePolicy';

const STORAGE_PREFIX = 'demoCompanySandbox';
const PERSIST_DEBOUNCE_MS = 250;

export type DemoSandboxSnapshot = {
	userId: string;
	companyId: string;
	clonedAt: number;
	mutated: boolean;
	baselines: Record<string, { data: any; status: number; savedAt: number }>;
	queries: Record<string, any>;
};

type ActivateResult = {
	created: boolean;
	restored: boolean;
	snapshot: DemoSandboxSnapshot;
};

let active: DemoSandboxSnapshot | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;

const READ_LIKE_URL = /\/(get-|list-|fetch-|search|query|load-|retrieve)/i;

/** POSTs that fetch data / suggestions — never mutate sandbox baselines. */
const READ_LIKE_POST_URL =
	/\/(dataset-category-suggestions|insights|keyword-lookup|folder-comparison|predictive-analytics|lookup-by-email|user-stats-trends|schema-stats|most-used|usage-stats|get-item-history|trending-tags)/i;

/**
 * Side-effect / telemetry POSTs. Intercepted as local success in demo mode but must
 * not invent rows in folder or content caches (ghost "Untitled" datasets / empty items).
 */
const DEMO_NOOP_MUTATION_URL =
	/\/(track-usage|bulk-track-usage|track-schema-view|track-schema-interaction|request-download|complete-tutorial|hide-first-tutorial)/i;

/** POSTs that update an existing resource — patch by id, never unshift a new list row. */
const DEMO_UPDATE_MUTATION_URL =
	/\/(update-item|change-title|update-settings|add-tag|add-description|add-notes|set-dataset-category|modify-folder|make-public|update-actions|replace-schema|flag-item|move-item)/i;

/** POSTs that create a new folder/dataset in list caches. */
const DEMO_FOLDER_LIST_CREATE_URL =
	/\/(add-new-data|upload-new-data|add-initial-data|\/data\/clone)/i;

const SESSION_PASSTHROUGH_PATTERNS = [
	'/user/switch-company',
	'/logout',
	'/login',
	'/register',
	'/session',
	'/otp',
	'/user/set-default-company',
];

const LIST_KEYS = [
	'dataContent',
	'records',
	'items',
	'folders',
	'entries',
	'events',
	'projects',
	'workPackages',
	'work_packages',
	'tasks',
	'data',
];

export const buildDemoSandboxStorageKey = (userId: string, companyId: string) =>
	`${STORAGE_PREFIX}:${userId}:${companyId}`;

export const isDemoSandboxActive = () => !!active;

export const getActiveDemoSandbox = () => active;

export const hasLocalDemoMutations = () => !!active?.mutated;

/** Read persisted sandbox from AsyncStorage without activating in-memory sandbox. */
export const loadDemoSnapshot = async (
	userId: string,
	companyId: string,
): Promise<DemoSandboxSnapshot | null> => {
	const safeUserId = String(userId || 'anon');
	const safeCompanyId = String(companyId);
	try {
		const raw = await AsyncStorage.getItem(
			buildDemoSandboxStorageKey(safeUserId, safeCompanyId),
		);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (parsed?.companyId !== safeCompanyId) return null;
		return {
			userId: safeUserId,
			companyId: safeCompanyId,
			clonedAt: parsed.clonedAt || Date.now(),
			mutated: !!parsed.mutated,
			baselines: parsed.baselines || {},
			queries: parsed.queries || {},
		};
	} catch (error) {
		console.warn('Failed to load demo company sandbox:', error);
		return null;
	}
};

export const hasPendingDemoMigration = async (
	userId: string,
	companyId: string,
): Promise<boolean> => {
	const snapshot = await loadDemoSnapshot(userId, companyId);
	return !!snapshot?.mutated;
};

export const clearDemoSnapshot = async (
	userId: string,
	companyId: string,
): Promise<void> => {
	try {
		await AsyncStorage.removeItem(
			buildDemoSandboxStorageKey(String(userId || 'anon'), String(companyId)),
		);
	} catch (error) {
		console.warn('Failed to clear demo company sandbox:', error);
	}
	if (
		active &&
		active.userId === String(userId || 'anon') &&
		active.companyId === String(companyId)
	) {
		deactivateDemoSandbox();
	}
};

export const isReadLikeRequest = (method?: string, url?: string) => {
	const normalizedMethod = String(method || 'get').toLowerCase();
	const normalizedUrl = String(url || '');
	if (
		normalizedMethod === 'get' ||
		normalizedMethod === 'head' ||
		normalizedMethod === 'options'
	) {
		return true;
	}
	return READ_LIKE_URL.test(normalizedUrl) || READ_LIKE_POST_URL.test(normalizedUrl);
};

export const isDemoNoopMutation = (url?: string) =>
	DEMO_NOOP_MUTATION_URL.test(String(url || ''));

export const isDemoUpdateMutation = (url?: string) =>
	DEMO_UPDATE_MUTATION_URL.test(String(url || ''));

export const isDemoFolderListCreate = (url?: string) =>
	DEMO_FOLDER_LIST_CREATE_URL.test(String(url || ''));

export const isDemoSessionPassthrough = (url?: string) => {
	const normalized = String(url || '').toLowerCase();
	return SESSION_PASSTHROUGH_PATTERNS.some((pattern) =>
		normalized.includes(pattern)
	);
};

export const buildDemoRequestKey = (config: {
	method?: string;
	url?: string;
	params?: any;
	data?: any;
}) => {
	const method = String(config?.method || 'get').toLowerCase();
	const url = String(config?.url || '').split('?')[0];
	let paramsKey = '';
	let bodyKey = '';
	try {
		paramsKey = config?.params ? JSON.stringify(config.params) : '';
	} catch {
		paramsKey = '';
	}
	try {
		bodyKey = stableDemoRequestBodyKey(config?.data);
	} catch {
		bodyKey = '';
	}
	return `${method}:${url}:${paramsKey}:${bodyKey}`;
};

const DEMO_REQUEST_BODY_KEYS = [
	'id',
	'_id',
	'dataId',
	'itemId',
	'contentId',
	'folderId',
	'type',
	'page',
	'limit',
	'pageSize',
	'sortBy',
	'sortOrder',
	'dataFilter',
	'filter',
];

export const stableDemoRequestBodyKey = (body: any) => {
	if (body == null) return '';
	let parsed = body;
	if (typeof parsed === 'string') {
		try {
			parsed = JSON.parse(parsed);
		} catch {
			return parsed;
		}
	}
	if (typeof parsed !== 'object') return '';
	const picked: Record<string, unknown> = {};
	for (const key of DEMO_REQUEST_BODY_KEYS) {
		if (parsed[key] !== undefined && parsed[key] !== null) {
			picked[key] = parsed[key];
		}
	}
	if (Object.keys(picked).length === 0) {
		return '';
	}
	return JSON.stringify(picked);
};

export const extractResourceId = (
	url?: string,
	body?: any,
	params?: any
): string | null => {
	const fromBody =
		body?._id ||
		body?.id ||
		body?.dataId ||
		body?.itemId ||
		body?.contentId ||
		body?.data?._id ||
		body?.data?.id;
	if (fromBody) return String(fromBody);
	const fromParams = params?.id || params?.dataId || params?.itemId;
	if (fromParams) return String(fromParams);
	const match = String(url || '').match(/\/([a-f0-9]{24})(?:\/|$|\?)/i);
	return match ? match[1] : null;
};

/**
 * Folder/dataset id for data-content routes.
 * `/data/add-data-content/:userId/:dataId` — use :dataId (2nd segment), not :userId.
 * `/data/get-data-content/:dataId` — use :dataId.
 */
export const extractDemoDataFolderId = (
	url?: string,
	body?: any,
	params?: any
): string | null => {
	const path = String(url || '').split('?')[0];
	const addMatch = path.match(
		/\/data\/add-data-content\/[^/]+\/([^/?#]+)/i
	);
	if (addMatch?.[1] && addMatch[1] !== 'undefined') {
		return String(addMatch[1]);
	}
	const getMatch = path.match(/\/data\/get-data-content(?:-paginated)?\/([^/?#]+)/i);
	if (getMatch?.[1] && getMatch[1] !== 'undefined') {
		return String(getMatch[1]);
	}
	const fromBody =
		body?.dataId ||
		(typeof body?.id === 'string' || typeof body?.id === 'number'
			? body.id
			: null);
	if (fromBody) return String(fromBody);
	const fromParams = params?.dataId || params?.id;
	if (fromParams) return String(fromParams);
	return null;
};

const isAddDataContentUrl = (url?: string) =>
	/\/data\/add-data-content\//i.test(String(url || ''));

const isNestedProjectMutationUrl = (url?: string) =>
	/\/projects\/(add-task|add-workpackage|delete-task|delete-wp|duplicate-task|change-task|modify-task|change-wp|update-workpackage)/i.test(
		String(url || '')
	) || /\/projects\/[^/]+\/tasks\//i.test(String(url || ''));

const isProjectCreateMutationUrl = (url?: string) =>
	/\/projects\/(add-task|add-workpackage|add-project)/i.test(String(url || ''));

const itemId = (item: any) => item?._id ?? item?.id ?? item?.dataId;

/** Resolve nested project / WP / task ids from common API body shapes. */
export const extractDemoProjectMutationIds = (url?: string, body?: any) => {
	const nested =
		body?.id && typeof body.id === 'object' && !Array.isArray(body.id)
			? body.id
			: {};
	const path = String(url || '').split('?')[0];
	const moveMatch = path.match(
		/\/projects\/([^/]+)\/tasks\/([^/]+)\/(?:move|bulk-move)/i
	);
	return {
		projectId:
			body?.projectId ||
			nested.projectId ||
			body?.dataId ||
			moveMatch?.[1] ||
			null,
		workPackageId:
			body?.workPackageId ||
			body?.wpId ||
			body?.extraId ||
			nested.workPackageId ||
			nested.extraId ||
			null,
		taskId:
			body?.taskId ||
			nested.taskId ||
			nested._id ||
			moveMatch?.[2] ||
			null,
	};
};

/**
 * API wrappers often send `{ userId, project }`, `{ userId, calendar }`, `{ task }`.
 * Demo list caches store the entity itself — unwrap before insert.
 */
export const unwrapDemoMutationEntity = (url?: string, body?: any): any => {
	if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
	const path = String(url || '').toLowerCase();

	if (path.includes('/projects/add-project') && body.project && typeof body.project === 'object') {
		return body.project;
	}
	if (path.includes('/projects/add-task') && body.task && typeof body.task === 'object') {
		return body.task;
	}
	if (
		path.includes('/projects/add-workpackage') &&
		body.workPackage &&
		typeof body.workPackage === 'object'
	) {
		return body.workPackage;
	}
	if (
		path.includes('/calendar/add-calendar-entry') &&
		body.calendar &&
		typeof body.calendar === 'object'
	) {
		return body.calendar;
	}
	return body;
};

export const extractDemoProjectScopeId = (
	url?: string,
	body?: any
): string | null => {
	if (body?.projectId) return String(body.projectId);
	if (body?.dataId) return String(body.dataId);
	const path = String(url || '').split('?')[0];
	const getProjectMatch = path.match(/\/get-project\/([^/?#]+)/i);
	if (getProjectMatch?.[1]) return String(getProjectMatch[1]);
	const projectsIdMatch = path.match(/\/projects\/(?:[^/]+\/)?([a-f0-9]{24})(?:\/|$)/i);
	if (projectsIdMatch?.[1] && !path.includes('/add-')) {
		return String(projectsIdMatch[1]);
	}
	return null;
};

const findWorkPackageArray = (node: any): any[] | null => {
	if (!node || typeof node !== 'object') return null;
	if (Array.isArray(node.workPackages)) return node.workPackages;
	if (Array.isArray(node.work_packages)) return node.work_packages;
	return null;
};

/** Insert a task under the matching work package inside a project payload.
 * Also appends to flat `tasks` arrays used by GET /projects/:userId/tasks/:projectId.
 * Writes both `task_list` and `tasks` so UI + hydrate paths stay in sync.
 */
export const insertDemoProjectTask = (
	payload: any,
	workPackageId: string | null | undefined,
	task: any
): any => {
	if (payload == null || !task) return payload;
	let clone: any;
	try {
		clone = JSON.parse(JSON.stringify(payload));
	} catch {
		return payload;
	}

	const enrichedTask = {
		...task,
		...(workPackageId
			? {
					workPackageId,
					work_package_id: workPackageId,
					wpId: workPackageId,
				}
			: {}),
	};

	const writeTaskLists = (wp: any) => {
		const existing = Array.isArray(wp.task_list)
			? wp.task_list
			: Array.isArray(wp.tasks)
				? wp.tasks
				: [];
		const next = [enrichedTask, ...existing];
		wp.task_list = next;
		wp.tasks = next;
	};

	const tryInsertNested = (node: any): boolean => {
		if (!node || typeof node !== 'object') return false;
		const wps = findWorkPackageArray(node);
		if (Array.isArray(wps)) {
			if (workPackageId) {
				const wp = wps.find(
					(item) => String(itemId(item)) === String(workPackageId)
				);
				if (wp) {
					writeTaskLists(wp);
					return true;
				}
			}
			if (wps[0]) {
				writeTaskLists(wps[0]);
				return true;
			}
		}
		if (node.project && tryInsertNested(node.project)) return true;
		if (node.data && !Array.isArray(node.data) && tryInsertNested(node.data)) {
			return true;
		}
		return false;
	};

	/** Flat task list payloads from getProjectTasks */
	const tryAppendFlatTasks = (node: any): boolean => {
		if (!node || typeof node !== 'object') return false;
		const looksLikeTaskFeed =
			Array.isArray(node.tasks) &&
			(node.taskCount != null ||
				node.workPackages != null ||
				typeof node.total === 'number' ||
				(node.tasks[0] &&
					(node.tasks[0].workPackageId != null ||
						node.tasks[0].task_title != null ||
						node.tasks[0].title != null)));
		if (looksLikeTaskFeed || (Array.isArray(node.tasks) && !findWorkPackageArray(node))) {
			node.tasks.unshift(enrichedTask);
			if (typeof node.taskCount === 'number') {
				node.taskCount += 1;
			}
			return true;
		}
		if (node.data && !Array.isArray(node.data) && tryAppendFlatTasks(node.data)) {
			return true;
		}
		return false;
	};

	tryInsertNested(clone);
	tryAppendFlatTasks(clone);
	return clone;
};

/** Insert a work package into a project payload. */
export const insertDemoWorkPackage = (payload: any, workPackage: any): any => {
	if (payload == null || !workPackage) return payload;
	let clone: any;
	try {
		clone = JSON.parse(JSON.stringify(payload));
	} catch {
		return payload;
	}

	const tryInsert = (node: any): boolean => {
		if (!node || typeof node !== 'object') return false;
		const wps = findWorkPackageArray(node);
		if (Array.isArray(wps)) {
			wps.unshift(workPackage);
			return true;
		}
		if (node.project && tryInsert(node.project)) return true;
		if (node.data && !Array.isArray(node.data) && tryInsert(node.data)) return true;
		return false;
	};

	tryInsert(clone);
	return clone;
};

const visitProjectNodes = (node: any, visitor: (n: any) => boolean): boolean => {
	if (!node || typeof node !== 'object') return false;
	if (visitor(node)) return true;
	if (node.project && visitProjectNodes(node.project, visitor)) return true;
	if (node.data && !Array.isArray(node.data) && visitProjectNodes(node.data, visitor)) {
		return true;
	}
	return false;
};

/** Patch or remove a nested task / work package inside project or tasks payloads. */
export const patchDemoNestedProjectEntity = (
	payload: any,
	opts: {
		workPackageId?: string | null;
		taskId?: string | null;
		updates?: Record<string, any> | null;
		remove?: boolean;
	}
): any => {
	if (payload == null) return payload;
	let clone: any;
	try {
		clone = JSON.parse(JSON.stringify(payload));
	} catch {
		return payload;
	}

	const { workPackageId, taskId, updates, remove } = opts;
	const wpIdStr = workPackageId != null ? String(workPackageId) : null;
	const taskIdStr = taskId != null ? String(taskId) : null;

	visitProjectNodes(clone, (node) => {
		// Flat task feed: `{ tasks: [...] }`
		if (taskIdStr && Array.isArray(node.tasks) && !findWorkPackageArray(node)) {
			const idx = node.tasks.findIndex(
				(t: any) => String(itemId(t)) === taskIdStr
			);
			if (idx >= 0) {
				if (remove) {
					node.tasks.splice(idx, 1);
					if (typeof node.taskCount === 'number') {
						node.taskCount = Math.max(0, node.taskCount - 1);
					}
				} else if (updates) {
					node.tasks[idx] = { ...node.tasks[idx], ...updates };
				}
				return true;
			}
		}

		const wps = findWorkPackageArray(node);
		if (!Array.isArray(wps)) return false;

		if (taskIdStr) {
			for (const wp of wps) {
				if (wpIdStr && String(itemId(wp)) !== wpIdStr) continue;
				const list = Array.isArray(wp.task_list)
					? wp.task_list
					: Array.isArray(wp.tasks)
						? wp.tasks
						: null;
				if (!list) continue;
				const idx = list.findIndex((t: any) => String(itemId(t)) === taskIdStr);
				if (idx < 0) continue;
				if (remove) {
					list.splice(idx, 1);
				} else if (updates) {
					list[idx] = { ...list[idx], ...updates };
				}
				wp.task_list = list;
				wp.tasks = list;
				return true;
			}
			return false;
		}

		if (wpIdStr) {
			const idx = wps.findIndex((wp: any) => String(itemId(wp)) === wpIdStr);
			if (idx < 0) return false;
			if (remove) {
				wps.splice(idx, 1);
			} else if (updates) {
				wps[idx] = { ...wps[idx], ...updates };
			}
			return true;
		}

		return false;
	});

	return clone;
};

export const collectArrays = (node: any, acc: any[][] = []): any[][] => {
	if (!node || typeof node !== 'object') return acc;
	if (Array.isArray(node)) {
		acc.push(node);
		node.forEach((child) => collectArrays(child, acc));
		return acc;
	}
	Object.values(node).forEach((value) => collectArrays(value, acc));
	return acc;
};

const insertIntoKnownList = (root: any, item: any): boolean => {
	if (!root || typeof root !== 'object') return false;
	for (const key of LIST_KEYS) {
		if (Array.isArray(root[key])) {
			root[key].unshift(item);
			return true;
		}
	}
	return false;
};

export const patchDemoPayload = (
	payload: any,
	method: string,
	body: any,
	id: string | null,
	options?: { nestDataContentFields?: boolean }
) => {
	if (payload == null) return payload;
	let clone: any;
	try {
		clone = JSON.parse(JSON.stringify(payload));
	} catch {
		return payload;
	}

	const normalizedMethod = String(method || '').toLowerCase();
	const record =
		body?.data && typeof body.data === 'object' && !Array.isArray(body.data)
			? { ...body, ...body.data }
			: body;

	if (normalizedMethod === 'delete' && id) {
		const arrays = collectArrays(clone);
		for (const arr of arrays) {
			for (let i = arr.length - 1; i >= 0; i -= 1) {
				if (String(itemId(arr[i])) === String(id)) {
					arr.splice(i, 1);
				}
			}
		}
		return clone;
	}

	if ((normalizedMethod === 'put' || normalizedMethod === 'patch') && id) {
		const arrays = collectArrays(clone);
		let found = false;
		for (const arr of arrays) {
			const index = arr.findIndex(
				(item) => String(itemId(item)) === String(id)
			);
			if (index >= 0) {
				arr[index] = {
					...arr[index],
					...record,
					_id: arr[index]._id || id,
					updatedAt: new Date().toISOString(),
				};
				found = true;
			}
		}
		if (!found && clone.data && typeof clone.data === 'object' && !Array.isArray(clone.data)) {
			Object.assign(clone.data, record);
		}
		return clone;
	}

	if (normalizedMethod === 'post' && record) {
		const createdId = record._id || record.id || `local-${Date.now()}`;
		const {
			_id: _ignoredId,
			id: _ignoredAltId,
			data: existingData,
			...fieldValues
		} = record;
		const nestedFields =
			existingData && typeof existingData === 'object' && !Array.isArray(existingData)
				? existingData
				: fieldValues;
		const newItem = options?.nestDataContentFields
			? {
					_id: createdId,
					data: nestedFields,
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
					syncMetadata: { localOnly: true },
				}
			: {
					...record,
					_id: createdId,
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
					syncMetadata: { localOnly: true },
				};
		if (
			insertIntoKnownList(clone, newItem) ||
			insertIntoKnownList(clone.data, newItem) ||
			insertIntoKnownList(clone.data?.data, newItem)
		) {
			return clone;
		}
		const arrays = collectArrays(clone);
		if (arrays[0]) {
			arrays[0].unshift(newItem);
		}
		return clone;
	}

	return clone;
};

const persistActive = (immediate = false) => {
	if (!active) return Promise.resolve();
	const snapshot = active;
	const write = () =>
		AsyncStorage.setItem(
			buildDemoSandboxStorageKey(snapshot.userId, snapshot.companyId),
			JSON.stringify(snapshot)
		).catch((error) => {
			console.warn('Failed to persist demo company sandbox:', error);
		});

	if (immediate) {
		if (persistTimer) {
			clearTimeout(persistTimer);
			persistTimer = null;
		}
		return write();
	}

	if (persistTimer) clearTimeout(persistTimer);
	persistTimer = setTimeout(() => {
		persistTimer = null;
		write();
	}, PERSIST_DEBOUNCE_MS);
	return Promise.resolve();
};

export const deactivateDemoSandbox = () => {
	active = null;
};

/**
 * Remove rows previously invented by treating analytics/category POSTs as creates.
 * Ghosts typically lack dataset titles and carry telemetry/category-dismiss fields.
 */
export const isDemoGhostListItem = (item: any): boolean => {
	if (!item || typeof item !== 'object') return false;
	if (!item.syncMetadata?.localOnly) return false;
	const hasTitle =
		Boolean(String(item.dataTitle || item.title || item.name || '').trim()) ||
		Boolean(String(item.data?.title || item.data?.name || '').trim());
	if (hasTitle) return false;
	const looksLikeTelemetry =
		item.action != null ||
		item.metadata != null ||
		item.dismiss != null ||
		item.category != null ||
		(item.dataId != null && item.dataSchema !== undefined) ||
		(item.dataId != null && Array.isArray(item.dataTags) && !item.dataContent);
	return looksLikeTelemetry;
};

const scrubGhostArrays = (node: any): boolean => {
	if (!node || typeof node !== 'object') return false;
	let changed = false;
	if (Array.isArray(node)) {
		for (let i = node.length - 1; i >= 0; i -= 1) {
			if (isDemoGhostListItem(node[i])) {
				node.splice(i, 1);
				changed = true;
			} else if (scrubGhostArrays(node[i])) {
				changed = true;
			}
		}
		return changed;
	}
	for (const value of Object.values(node)) {
		if (scrubGhostArrays(value)) changed = true;
	}
	return changed;
};

export const scrubDemoGhostBaselines = (snapshot: DemoSandboxSnapshot | null): boolean => {
	if (!snapshot?.baselines) return false;
	let changed = false;
	for (const key of Object.keys(snapshot.baselines)) {
		if (!/get-data-folders|get-data-content/.test(key)) continue;
		const entry = snapshot.baselines[key];
		if (!entry?.data) continue;
		try {
			const clone = JSON.parse(JSON.stringify(entry.data));
			if (scrubGhostArrays(clone)) {
				snapshot.baselines[key] = { ...entry, data: clone, savedAt: Date.now() };
				changed = true;
			}
		} catch {
			/* keep original */
		}
	}
	if (changed && snapshot.queries) {
		Object.keys(snapshot.queries).forEach((key) => {
			if (
				key.includes('individualData') ||
				/dataFolders|get-data-folders|get-data-content/i.test(key)
			) {
				delete snapshot.queries[key];
			}
		});
	}
	return changed;
};

export const activateDemoSandbox = async ({
	userId,
	companyId,
}: {
	userId: string;
	companyId: string;
}): Promise<ActivateResult> => {
	const safeUserId = String(userId || 'anon');
	const safeCompanyId = String(companyId);
	if (
		active &&
		active.userId === safeUserId &&
		active.companyId === safeCompanyId
	) {
		if (scrubDemoGhostBaselines(active)) {
			persistActive(true);
		}
		return { created: false, restored: true, snapshot: active };
	}

	let restored: DemoSandboxSnapshot | null = null;
	try {
		const raw = await AsyncStorage.getItem(
			buildDemoSandboxStorageKey(safeUserId, safeCompanyId)
		);
		if (raw) {
			const parsed = JSON.parse(raw);
			if (parsed?.companyId === safeCompanyId) {
				restored = {
					userId: safeUserId,
					companyId: safeCompanyId,
					clonedAt: parsed.clonedAt || Date.now(),
					mutated: !!parsed.mutated,
					baselines: parsed.baselines || {},
					queries: parsed.queries || {},
				};
			}
		}
	} catch (error) {
		console.warn('Failed to restore demo company sandbox:', error);
	}

	if (restored) {
		const scrubbed = scrubDemoGhostBaselines(restored);
		active = restored;
		if (scrubbed) {
			persistActive(true);
		}
		return { created: false, restored: true, snapshot: restored };
	}

	const created: DemoSandboxSnapshot = {
		userId: safeUserId,
		companyId: safeCompanyId,
		clonedAt: Date.now(),
		mutated: false,
		baselines: {},
		queries: {},
	};
	active = created;
	persistActive(true);
	return { created: true, restored: false, snapshot: created };
};

export const syncDemoSandboxForCompany = async (
	company: { _id?: string; demo?: boolean } | null | undefined,
	userId?: string
): Promise<ActivateResult | { created: false; restored: false; snapshot: null }> => {
	if (company?.demo && company._id) {
		return activateDemoSandbox({
			userId: String(userId || 'anon'),
			companyId: String(company._id),
		});
	}
	deactivateDemoSandbox();
	return { created: false, restored: false, snapshot: null };
};

export const seedDemoCompanyDatasets = async (
	request: (config: { method: string; url: string; data?: any }) => Promise<any>
) => {
	if (!isDemoSandboxActive() || hasLocalDemoMutations()) return;
	try {
		await request({
			method: 'post',
			url: '/data/get-data-folders-paginated',
			data: { page: 1, limit: 50, sortBy: 'updatedAt', sortOrder: 'desc' },
		});
		await request({
			method: 'post',
			url: '/data/get-data-folders',
			data: { dataFilter: 'all' },
		});
	} catch (error) {
		console.warn('Failed to clone demo company datasets:', error);
	}
};

const getStoreState = () => {
	try {
		const { store } = require('@reduxLocal/persistState');
		return store?.getState?.() || null;
	} catch {
		return null;
	}
};

export const ensureDemoSandboxFromStore = async () => {
	const state = getStoreState();
	if (!state) return;
	const profile = state?.app?.selectedProfile;
	const userId =
		state?.promiseData?.data?.userData?._id ||
		state?.promiseData?.data?.userData?.id;
	if (profile?.demo && profile?.id) {
		await activateDemoSandbox({
			userId: String(userId || 'anon'),
			companyId: String(profile.id),
		});
		return;
	}
	if (profile?.id && !profile.demo && active) {
		deactivateDemoSandbox();
	}
};

export const cacheDemoGetResponse = (config: any, data: any, status = 200) => {
	if (!active || config?._demoSandboxLocal) return;
	if (status >= 400) return;
	if (!isReadLikeRequest(config?.method, config?.url)) return;
	const key = buildDemoRequestKey(config);
	active.baselines[key] = {
		data,
		status,
		savedAt: Date.now(),
	};
	persistActive();
};

export const getCachedDemoResponse = (config: any) => {
	if (!active) return null;
	return active.baselines[buildDemoRequestKey(config)] || null;
};

export const DEMO_QUERY_PASSTHROUGH_ROOTS = new Set([
	'companyList',
	'subscriptionPlan',
	'billingInfo',
	'planData',
	'planSubscriptions',
]);

export const toDemoQueryStorageKey = (queryKey: unknown, pageParam?: unknown) => {
	try {
		if (pageParam !== undefined) {
			return JSON.stringify({ queryKey, pageParam });
		}
		return JSON.stringify(queryKey);
	} catch {
		return String(queryKey);
	}
};

export const isPassthroughDemoQueryKey = (queryKey: unknown) => {
	const root = Array.isArray(queryKey) ? queryKey[0] : queryKey;
	return typeof root === 'string' && DEMO_QUERY_PASSTHROUGH_ROOTS.has(root);
};

export const persistDemoQueryResult = (
	queryKey: unknown,
	data: any,
	pageParam?: unknown
) => {
	if (!active || data === undefined) return;
	if (isPassthroughDemoQueryKey(queryKey)) return;
	if (!active.queries) active.queries = {};
	active.queries[toDemoQueryStorageKey(queryKey, pageParam)] = data;
	persistActive();
};

export const getDemoQueryResult = (queryKey: unknown, pageParam?: unknown) => {
	if (!active?.queries) return undefined;
	if (!(toDemoQueryStorageKey(queryKey, pageParam) in active.queries)) {
		return undefined;
	}
	return active.queries[toDemoQueryStorageKey(queryKey, pageParam)];
};

export const markDemoQueriesMutated = () => {
	if (!active) return;
	active.mutated = true;
	persistActive(true);
};

const urlsRelated = (baselineKey: string, url?: string) => {
	const requestUrl = String(url || '').toLowerCase();
	const baselineUrl = baselineKey.split(':')[1] || '';
	if (!requestUrl || !baselineUrl) return false;
	const markers = ['/data/', '/projects/', '/calendar/', '/knowledge/', '/feed/', '/flow'];
	return markers.some(
		(marker) => requestUrl.includes(marker) && baselineUrl.includes(marker)
	);
};

export const applyDemoMutation = (config: any) => {
	if (!active) {
		return {
			success: true,
			localOnly: true,
			data: config?.data || {},
		};
	}

	const method = String(config?.method || 'post').toLowerCase();
	const body = config?.data;
	const url = String(config?.url || '');

	// Telemetry / analytics: acknowledge locally, never invent list/content rows.
	if (isDemoNoopMutation(url)) {
		return {
			success: true,
			localOnly: true,
			data: {},
			message: 'Demo local no-op',
		};
	}

	const folderId = extractDemoDataFolderId(url, body, config?.params);
	const nestedIds = extractDemoProjectMutationIds(url, body);
	const projectScopeId =
		extractDemoProjectScopeId(url, body) || nestedIds.projectId;
	const nestedProject = isNestedProjectMutationUrl(url);
	const projectCreate = isProjectCreateMutationUrl(url);
	const id = extractResourceId(url, body, config?.params);
	const mutationFolderId = String(
		folderId ||
			body?.dataId ||
			(nestedProject || /\/projects\//i.test(url) ? projectScopeId : '') ||
			''
	);
	const nestDataContentFields = isAddDataContentUrl(url);
	const entity = unwrapDemoMutationEntity(url, body);
	const updateMutation = isDemoUpdateMutation(url);
	const folderListCreate = isDemoFolderListCreate(url);
	// Only force "create" (null id) for true create routes — not change/delete/update POSTs
	const itemIdForPatch =
		(nestDataContentFields || projectCreate) && method === 'post' && !updateMutation
			? null
			: id;
	/** POST update routes must reuse put/patch semantics so we don't unshift ghosts. */
	const patchMethod =
		method === 'post' && updateMutation && itemIdForPatch
			? 'patch'
			: method;

	const createdId =
		entity?._id ||
		body?._id ||
		((nestDataContentFields || projectCreate || /\/(add-calendar)/i.test(url)) &&
		method === 'post' &&
		!updateMutation
			? `local-${Date.now()}`
			: id || body?.id || entity?.id || `local-${Date.now()}`);

	const entityWithId =
		method === 'post' &&
		!updateMutation &&
		entity &&
		typeof entity === 'object' &&
		!Array.isArray(entity)
			? { ...entity, _id: createdId }
			: entity;

	const nestedUpdates = (() => {
		if (!nestedProject && !/\/projects\/[^/]+\/tasks\//i.test(url)) return null;
		if (projectCreate) return null;
		const updates: Record<string, any> = {};
		if (body?.status != null) updates.status = body.status;
		if (body?.title != null) {
			updates.title = body.title;
			updates.task_title = body.title;
			updates.work_package_title = body.title;
		}
		if (body?.description != null) updates.description = body.description;
		if (body?.destinationStatus != null) updates.status = body.destinationStatus;
		if (body?.task && typeof body.task === 'object') Object.assign(updates, body.task);
		if (body?.workPackage && typeof body.workPackage === 'object') {
			Object.assign(updates, body.workPackage);
		}
		const skipKeys = new Set([
			'userId',
			'projectId',
			'workPackageId',
			'wpId',
			'taskId',
			'extraId',
			'dataId',
			'id',
			'task',
			'workPackage',
			'sourceStatus',
			'destinationStatus',
			'sourceIndex',
			'destinationIndex',
		]);
		for (const key of Object.keys(body || {})) {
			if (skipKeys.has(key)) continue;
			if (body[key] !== undefined) updates[key] = body[key];
		}
		return Object.keys(updates).length ? updates : null;
	})();

	const patchBaseline = (baselineData: any) => {
		if (/\/projects\/add-task/i.test(url) && method === 'post') {
			return insertDemoProjectTask(
				baselineData,
				nestedIds.workPackageId || body?.workPackageId || body?.extraId,
				entityWithId
			);
		}
		if (/\/projects\/add-workpackage/i.test(url) && method === 'post') {
			return insertDemoWorkPackage(baselineData, entityWithId);
		}
		if (/\/projects\/delete-task/i.test(url) || (/\/projects\/[^/]+\/tasks\//i.test(url) && method === 'delete')) {
			return patchDemoNestedProjectEntity(baselineData, {
				workPackageId: nestedIds.workPackageId,
				taskId: nestedIds.taskId || id,
				remove: true,
			});
		}
		if (/\/projects\/delete-wp/i.test(url)) {
			return patchDemoNestedProjectEntity(baselineData, {
				workPackageId: nestedIds.workPackageId || id,
				remove: true,
			});
		}
		if (
			(/\/projects\/(change-task|modify-task|change-wp|update-workpackage)/i.test(url) ||
				/\/projects\/[^/]+\/tasks\/[^/]+\/move/i.test(url)) &&
			nestedUpdates
		) {
			return patchDemoNestedProjectEntity(baselineData, {
				workPackageId: nestedIds.workPackageId,
				taskId: nestedIds.taskId,
				updates: nestedUpdates,
				remove: false,
			});
		}
		return patchDemoPayload(
			baselineData,
			patchMethod,
			entityWithId,
			itemIdForPatch,
			{ nestDataContentFields }
		);
	};

	Object.keys(active.baselines).forEach((key) => {
		if (!urlsRelated(key, url)) return;
		if (mutationFolderId && !key.includes(mutationFolderId)) return;
		if (!mutationFolderId && /get-data-content/.test(key)) return;
		// Never mutate schema definition caches
		if (/get-.*schema|\/schema/i.test(key)) return;
		// Only dataset-create routes may insert into folder list caches
		if (/get-data-folders/.test(key) && patchMethod === 'post' && !folderListCreate) {
			return;
		}
		// Only add-data-content may insert into content list caches
		if (/get-data-content/.test(key) && patchMethod === 'post' && !nestDataContentFields) {
			return;
		}
		// List creates should not pollute detail / sibling caches
		if (
			/\/projects\/add-project/i.test(url) &&
			/get-project\//i.test(key)
		) {
			return;
		}
		if (
			/\/calendar\/add-calendar-entry/i.test(url) &&
			/get-calendars/i.test(key)
		) {
			return;
		}
		if (
			/\/calendar\/[^/]+\/add-calendar/i.test(url) &&
			/get-calendar(?!s)/i.test(key)
		) {
			return;
		}
		active.baselines[key] = {
			...active.baselines[key],
			data: patchBaseline(active.baselines[key].data),
			savedAt: Date.now(),
		};
	});

	// Drop stale React Query sandbox snapshots so refetches hit patched baselines
	if (active.queries) {
		Object.keys(active.queries).forEach((key) => {
			const dropForFolder =
				mutationFolderId &&
				key.includes(mutationFolderId) &&
				(key.includes('individualData') ||
					/get-data-content/i.test(key) ||
					/project/i.test(key));
			const dropForNestedProject =
				(nestedProject || /\/projects\//i.test(url)) &&
				(/project/i.test(key) ||
					(projectScopeId ? key.includes(String(projectScopeId)) : false));
			const dropForProjects =
				/\/projects\/add-project/i.test(url) && /projects/i.test(key);
			const dropForCalendar =
				/\/calendar\/add-calendar/i.test(url) && /calendar/i.test(key);
			if (
				dropForFolder ||
				dropForNestedProject ||
				dropForProjects ||
				dropForCalendar
			) {
				delete active.queries[key];
			}
		});
	}

	active.mutated = true;
	persistActive(true);

	// Match real API: { success, data: <newItemId> }
	return {
		success: true,
		localOnly: true,
		_id: createdId,
		id: createdId,
		data: createdId,
		item: entityWithId,
		message: 'Saved to your local demo copy',
	};
};

export const createLocalAxiosAdapter = (data: any, status = 200) => {
	return async (config: any) => ({
		data,
		status,
		statusText: status >= 200 && status < 300 ? 'OK' : 'Error',
		headers: { 'x-demo-sandbox': 'local' },
		config,
		request: { _demoSandbox: true },
	});
};

export const attachDemoSandboxAdapter = async (config: any) => {
	await ensureDemoSandboxFromStore();
	if (!isDemoSandboxActive()) return config;
	if (isDemoSessionPassthrough(config?.url)) return config;

	if (isReadLikeRequest(config?.method, config?.url)) {
		const cached = getCachedDemoResponse(config);
		if (cached && hasLocalDemoMutations()) {
			config.adapter = createLocalAxiosAdapter(cached.data, cached.status || 200);
			config._demoSandboxLocal = true;
		}
		return config;
	}

	if (!isMutationMethod(config?.method)) return config;

	if (isSensitiveOnlineRequiredRoute(config?.url)) {
		config.adapter = createLocalAxiosAdapter(
			{
				error: 'DEMO_READ_ONLY',
				code: 'DEMO_READ_ONLY',
				message:
					'Account and billing changes are not available on demo companies.',
			},
			403
		);
		config._demoSandboxLocal = true;
		return config;
	}

	const payload = applyDemoMutation(config);
	config.adapter = createLocalAxiosAdapter(payload, 200);
	config._demoSandboxLocal = true;
	return config;
};
